import { useToast } from 'primevue/usetoast'
import { defineStore } from 'pinia'
import type { ToastMessageOptions } from 'primevue/toast'
import { t } from '@/i18n'

type Severity = Exclude<ToastMessageOptions['severity'], undefined>

const DURATION = 3500

/* Функция, а не словарь-константа: на уровне модуля заголовки застряли бы
   на языке, который стоял в момент загрузки приложения. */
function defaultTitle(severity: Severity): string | undefined {
  const titles: Partial<Record<Severity, string>> = {
    success: t('toast.success'),
    error: t('toast.error'),
    warn: t('toast.warning'),
    info: t('toast.info'),
  }
  return titles[severity]
}

/**
 * Обёртка над PrimeVue Toast. Один аргумент — это текст с дефолтным
 * заголовком по severity, два — заголовок и текст.
 */
export const useToastStore = defineStore('toast', () => {
  const toast = useToast()

  /** Уже висящие сообщения — второй раз то же самое не показываем. */
  const visible = new Set<string>()

  function fire(severity: Severity, title: string, text?: string): void {
    const key = `${severity}:${title}:${text ?? ''}`
    if (visible.has(key)) return

    visible.add(key)
    setTimeout(() => visible.delete(key), DURATION)

    toast.add({
      severity,
      summary: text ? title : (defaultTitle(severity) ?? ''),
      detail: text ?? title,
      life: DURATION,
    })
  }

  return {
    success: (title: string, text?: string) => fire('success', title, text),
    info: (title: string, text?: string) => fire('info', title, text),
    warning: (title: string, text?: string) => fire('warn', title, text),
    error: (title: string, text?: string) => fire('error', title, text),
  }
})
